import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReAuth } from 'api/Api';
import { DesignProps } from 'api/DesignApi';
import {
  Artifact,
  ArtifactCollection,
  ArtifactMedia,
  ArtifactMediaKind,
  ArtifactMediaUpload,
  ArtifactShare,
  ArtifactShareStatus,
  ArtifactTranscript,
  MediaUploadStatus,
  PublicArtifactBranding,
  PublicArtifactCollection,
  TranscriptionStatus
} from 'components/Artifacts/types';

type ArtifactEnvelope = {
  data?: unknown;
};

type ApiRequestConfig = {
  url: string;
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
};

type ArtifactCollectionEnvelope = {
  data?: {
    revision?: unknown;
    collectionRevision?: unknown;
    downloadZipUrl?: unknown;
    artifactsZipUrl?: unknown;
    downloadArchiveUrl?: unknown;
    downloadUrl?: unknown;
    items?: unknown[];
    artifacts?: unknown[];
  };
};

type PublicArtifactCollectionEnvelope = {
  data?: {
    share?: unknown;
    branding?: unknown;
    patient?: unknown;
    collection?: {
      revision?: unknown;
      collectionRevision?: unknown;
      items?: unknown[];
      artifacts?: unknown[];
    };
    revision?: unknown;
    collectionRevision?: unknown;
    items?: unknown[];
    artifacts?: unknown[];
    [key: string]: unknown;
  };
};

type SuccessEnvelope = {
  success?: boolean;
};

type ArtifactMediaUploadEnvelope = {
  data?: unknown;
};

type ArtifactShareEnvelope = {
  data?: unknown;
};

const MEDIA_UPLOAD_STATUSES: MediaUploadStatus[] = ['PENDING_UPLOAD', 'UPLOADED', 'PROCESSING', 'READY', 'FAILED'];
const TRANSCRIPTION_STATUSES: TranscriptionStatus[] = ['NOT_REQUESTED', 'QUEUED', 'PROCESSING', 'COMPLETED', 'FAILED'];
const ARTIFACT_SHARE_STATUSES: ArtifactShareStatus[] = ['ACTIVE', 'EXPIRED', 'REVOKED'];

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

const hasOwn = (record: Record<string, unknown>, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(record, key);

const asString = (value: unknown, fallback = ''): string => (typeof value === 'string' ? value : fallback);

const asStringOrNull = (value: unknown): string | null => (typeof value === 'string' && value.trim() ? value : null);

const pickFirstString = (source: Record<string, unknown>, keys: string[]): string | null => {
  for (const key of keys) {
    const value = asStringOrNull(source[key]);
    if (value) {
      return value;
    }
  }

  return null;
};

const asNumber = (value: unknown, fallback = 0): number => {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const asBoolean = (value: unknown, fallback = false): boolean => (typeof value === 'boolean' ? value : fallback);

const asStatus = <T extends string>(value: unknown, allowed: T[], fallback: T): T => {
  const candidate = typeof value === 'string' ? (value.toUpperCase() as T) : fallback;
  return allowed.includes(candidate) ? candidate : fallback;
};

const resolveMediaUrl = (value: string | null): string | null => {
  if (!value) {
    return null;
  }

  try {
    const apiBaseUrl = asStringOrNull(process.env.REACT_APP_API_URL);

    if (apiBaseUrl) {
      return new URL(value, apiBaseUrl).toString();
    }

    if (typeof window !== 'undefined' && window.location?.origin) {
      return new URL(value, window.location.origin).toString();
    }
  } catch {
    return value;
  }

  return value;
};

const getTranscriptDisplayText = (source: Record<string, unknown>): string | null =>
  asStringOrNull(source.text) ??
  asStringOrNull(source.editedText) ??
  asStringOrNull(source.editedTranscriptText) ??
  asStringOrNull(source.generatedTranscriptText) ??
  asStringOrNull(source.generatedText) ??
  asStringOrNull(source.transcriptText);

const getArtifactImageFallback = (source: Record<string, unknown>) => ({
  id: source.imageId ?? source.photoId ?? source.publicItemId ?? source.id,
  imageId: source.imageId,
  photoId: source.photoId,
  downloadUrl: source.downloadUrl,
  downloadPhotoUrl: source.downloadPhotoUrl,
  imageDownloadUrl: source.imageDownloadUrl,
  photoDownloadUrl: source.photoDownloadUrl,
  imageUrl: source.imageUrl,
  photoUrl: source.photoUrl,
  imageSignedUrl: source.imageSignedUrl,
  photoSignedUrl: source.photoSignedUrl,
  imageThumbnailUrl: source.imageThumbnailUrl,
  photoThumbnailUrl: source.photoThumbnailUrl,
  imageThumbnailSignedUrl: source.imageThumbnailSignedUrl,
  photoThumbnailSignedUrl: source.photoThumbnailSignedUrl,
  mimeType: source.imageMimeType ?? source.photoMimeType ?? source.mimeType,
  status: source.imageStatus ?? source.photoStatus ?? source.status,
  errorCode: source.imageErrorCode ?? source.photoErrorCode ?? source.errorCode
});

const getArtifactAudioFallback = (source: Record<string, unknown>) => ({
  id:
    source.audioId ??
    (typeof source.publicItemId === 'string' && source.publicItemId.trim()
      ? `${source.publicItemId}-audio`
      : typeof source.id === 'string' && source.id.trim()
        ? `${source.id}-audio`
        : undefined),
  audioId: source.audioId,
  downloadUrl: source.downloadUrl,
  downloadAudioUrl: source.downloadAudioUrl,
  audioDownloadUrl: source.audioDownloadUrl,
  audioUrl: source.audioUrl,
  audioSignedUrl: source.audioSignedUrl,
  mimeType: source.audioMimeType ?? source.mimeType,
  durationSec: source.audioDurationSec ?? source.durationSec,
  status: source.audioStatus ?? source.status,
  errorCode: source.audioErrorCode ?? source.errorCode
});

export const normalizeArtifactMedia = (value: unknown, fallbackValue?: unknown): ArtifactMedia | null => {
  const source = {
    ...asRecord(fallbackValue),
    ...asRecord(value)
  };
  const id = pickFirstString(source, [
    'photoId',
    'audioId',
    'imageId',
    'id',
    'mediaId',
    'uploadId',
    'photoUploadId',
    'audioUploadId',
    'assetId',
    'fileId'
  ]);
  const url = resolveMediaUrl(
    pickFirstString(source, [
      'url',
      'signedUrl',
      'mediaUrl',
      'fileUrl',
      'viewUrl',
      'src',
      'originalUrl',
      'photoUrl',
      'audioUrl',
      'imageUrl',
      'photoSignedUrl',
      'audioSignedUrl',
      'imageSignedUrl',
      'signedDownloadUrl',
      'downloadSignedUrl'
    ])
  );
  const downloadUrl = resolveMediaUrl(
    pickFirstString(source, [
      'downloadUrl',
      'downloadSignedUrl',
      'signedDownloadUrl',
      'imageDownloadUrl',
      'photoDownloadUrl',
      'audioDownloadUrl',
      'downloadPhotoUrl',
      'downloadAudioUrl'
    ])
  );
  const thumbnailUrl = resolveMediaUrl(
    pickFirstString(source, [
      'thumbnailUrl',
      'thumbnailSignedUrl',
      'previewUrl',
      'previewSignedUrl',
      'thumbUrl',
      'photoThumbnailUrl',
      'imageThumbnailUrl',
      'photoThumbnailSignedUrl',
      'imageThumbnailSignedUrl'
    ])
  );

  if (!id || !url) {
    return null;
  }

  return {
    id,
    url,
    downloadUrl,
    thumbnailUrl,
    mimeType: asStringOrNull(source.mimeType),
    durationSec: source.durationSec == null ? null : asNumber(source.durationSec, 0),
    status: asStatus(source.status, MEDIA_UPLOAD_STATUSES, url ? 'READY' : 'PROCESSING'),
    errorCode: asStringOrNull(source.errorCode)
  };
};

export const normalizeArtifactTranscript = (value: unknown, fallbackValue?: unknown): ArtifactTranscript => {
  const source = {
    ...asRecord(fallbackValue),
    ...asRecord(value)
  };
  const transcriptText = getTranscriptDisplayText(source);

  return {
    text: transcriptText,
    generatedText:
      asStringOrNull(source.generatedTranscriptText) ??
      asStringOrNull(source.generatedText) ??
      asStringOrNull(source.transcriptText),
    isEdited:
      asBoolean(source.isEdited) ||
      Boolean(asStringOrNull(source.editedText)) ||
      Boolean(asStringOrNull(source.editedTranscriptText)),
    status: asStatus(
      source.status ?? source.transcriptionStatus ?? (transcriptText ? 'COMPLETED' : undefined),
      TRANSCRIPTION_STATUSES,
      'NOT_REQUESTED'
    ),
    canRetry: asBoolean(source.canRetry ?? source.canRetryTranscription),
    retryCount: source.retryCount == null ? null : asNumber(source.retryCount, 0),
    errorCode: asStringOrNull(source.errorCode) ?? asStringOrNull(source.transcriptionError)
  };
};

export const normalizeArtifact = (value: unknown): Artifact => {
  const source = asRecord(value);
  const imageFallback = getArtifactImageFallback(source);
  const audioFallback = getArtifactAudioFallback(source);

  return {
    id: asStringOrNull(source.id) ?? asStringOrNull(source.publicItemId) ?? '',
    revision: asNumber(source.revision, 0),
    title: asString(source.title),
    description: asStringOrNull(source.description),
    memoryDate: asStringOrNull(source.memoryDate),
    memoryDateLabel: asStringOrNull(source.memoryDateLabel) ?? asStringOrNull(source.dateLabel),
    image: normalizeArtifactMedia(source.image ?? source.primaryImage ?? source.photo, imageFallback),
    audio: normalizeArtifactMedia(source.audio ?? source.storyAudio ?? source.voiceNote, audioFallback),
    transcript: normalizeArtifactTranscript(source.transcript, source),
    displayOrder: asNumber(source.displayOrder, 0),
    canEdit: typeof source.canEdit === 'boolean' ? source.canEdit : undefined,
    canDelete: typeof source.canDelete === 'boolean' ? source.canDelete : undefined,
    canShare: typeof source.canShare === 'boolean' ? source.canShare : undefined,
    createdAt: asStringOrNull(source.createdAt),
    updatedAt: asStringOrNull(source.updatedAt)
  };
};

export const normalizeArtifactCollectionResponse = (response: ArtifactCollectionEnvelope): ArtifactCollection => {
  const data = asRecord(response.data);
  const items = Array.isArray(data.items) ? data.items : Array.isArray(data.artifacts) ? data.artifacts : [];

  return {
    revision: asNumber(data.revision ?? data.collectionRevision, 0),
    downloadZipUrl: resolveMediaUrl(
      pickFirstString(data, ['downloadZipUrl', 'artifactsZipUrl', 'downloadArchiveUrl', 'downloadUrl'])
    ),
    items: items.map((item) => normalizeArtifact(item))
  };
};

export const normalizeArtifactMediaUpload = (value: unknown): ArtifactMediaUpload => {
  const source = asRecord(value);
  const uploadFieldsSource = asRecord(source.uploadFields);
  const uploadId = asStringOrNull(source.id) ?? asStringOrNull(source.uploadId) ?? '';
  const mediaType = asString(source.mediaType).toUpperCase();
  const mediaKindCandidate =
    source.mediaKind ?? (mediaType === 'AUDIO' ? 'AUDIO' : mediaType === 'PHOTO' ? 'IMAGE' : undefined);
  const normalizedMedia = normalizeArtifactMedia(source.media);
  const hasUploadUrl = Boolean(asStringOrNull(source.uploadUrl) ?? asStringOrNull(source.uploadSignedUrl));

  return {
    id: uploadId,
    artifactId: asStringOrNull(source.artifactId),
    mediaKind: asStatus(mediaKindCandidate, ['IMAGE', 'AUDIO'], 'IMAGE') as ArtifactMediaKind,
    media: normalizedMedia,
    uploadUrl: asStringOrNull(source.uploadUrl) ?? asStringOrNull(source.uploadSignedUrl),
    uploadFields: Object.keys(uploadFieldsSource).length
      ? Object.fromEntries(Object.entries(uploadFieldsSource).map(([key, entryValue]) => [key, asString(entryValue)]))
      : null,
    status: asStatus(
      source.status ?? normalizedMedia?.status,
      MEDIA_UPLOAD_STATUSES,
      uploadId && !hasUploadUrl ? 'UPLOADED' : 'PENDING_UPLOAD'
    ),
    errorCode: asStringOrNull(source.errorCode)
  };
};

export const normalizeArtifactShare = (value: unknown): ArtifactShare | null => {
  const sourceRecord = asRecord(value);
  const nestedShare = asRecord(sourceRecord.share);
  const nestedArtifactShare = asRecord(sourceRecord.artifactShare);
  const nestedInvite = asRecord(sourceRecord.invite);
  const source = Object.keys(nestedShare).length
    ? { ...sourceRecord, ...nestedShare }
    : Object.keys(nestedArtifactShare).length
      ? { ...sourceRecord, ...nestedArtifactShare }
      : sourceRecord;
  const recipientSource = asRecord(source.recipient);
  const recipientsSource = Array.isArray(source.recipients) ? asRecord(source.recipients[0]) : {};
  const contactSource = asRecord(source.contact);
  const inviteSource = asRecord(source.invite);
  const id =
    asStringOrNull(source.id) ??
    asStringOrNull(inviteSource.inviteId) ??
    asStringOrNull(inviteSource.id) ??
    asStringOrNull(nestedInvite.inviteId) ??
    asStringOrNull(nestedInvite.id);

  if (!id) {
    return null;
  }

  return {
    id,
    token: asStringOrNull(source.token),
    publicUrl: asStringOrNull(source.publicUrl),
    status: asStatus(source.status, ARTIFACT_SHARE_STATUSES, 'ACTIVE'),
    recipientName:
      pickFirstString(source, ['recipientName']) ??
      pickFirstString(inviteSource, ['recipientName', 'name', 'fullName', 'displayName']) ??
      pickFirstString(nestedInvite, ['recipientName', 'name', 'fullName', 'displayName']) ??
      pickFirstString(recipientsSource, ['name', 'fullName', 'displayName', 'recipientName']) ??
      pickFirstString(recipientSource, ['name', 'fullName', 'displayName', 'recipientName']) ??
      pickFirstString(contactSource, ['name', 'fullName', 'displayName', 'recipientName']),
    recipientEmail:
      pickFirstString(source, [
        'recipientEmail',
        'recipientEmailAddress',
        'inviteeEmail',
        'invitedEmail',
        'email',
        'emailAddress'
      ]) ??
      pickFirstString(inviteSource, [
        'recipientEmail',
        'recipientEmailAddress',
        'inviteeEmail',
        'invitedEmail',
        'email',
        'emailAddress'
      ]) ??
      pickFirstString(nestedInvite, [
        'recipientEmail',
        'recipientEmailAddress',
        'inviteeEmail',
        'invitedEmail',
        'email',
        'emailAddress'
      ]) ??
      pickFirstString(recipientsSource, ['email', 'recipientEmail', 'emailAddress', 'recipientEmailAddress']) ??
      pickFirstString(recipientSource, ['email', 'recipientEmail', 'emailAddress', 'recipientEmailAddress']) ??
      pickFirstString(contactSource, ['email', 'recipientEmail', 'emailAddress', 'recipientEmailAddress']),
    expiresAt: asStringOrNull(source.expiresAt),
    createdAt: asStringOrNull(source.createdAt),
    updatedAt: asStringOrNull(source.updatedAt),
    revokedAt: asStringOrNull(source.revokedAt)
  };
};

export const normalizePublicArtifactBranding = (value: unknown): PublicArtifactBranding => {
  const source = asRecord(value);
  const designProps = {
    ...asRecord(source.designProps),
    ...(typeof source.domainUrl === 'string' ? { domainUrl: source.domainUrl } : {}),
    ...(typeof source.companyName === 'string' ? { companyName: source.companyName } : {}),
    ...(typeof source.logoUrl === 'string' ? { logoUrl: source.logoUrl } : {})
  } as DesignProps;
  const organization = asRecord(source.organization);
  const tenantOrganization = asRecord(source.tenantOrganization);
  const normalizedDesignProps = Object.keys(designProps).length ? designProps : undefined;

  return {
    firstName: asStringOrNull(source.firstName),
    lastName: asStringOrNull(source.lastName),
    fullName: asStringOrNull(source.fullName) ?? asStringOrNull(source.patientDisplayName),
    signupUrl: asStringOrNull(normalizedDesignProps?.domainUrl),
    tenantOrganizationName:
      asStringOrNull(source.tenantOrganizationName) ??
      asStringOrNull(source.organizationName) ??
      asStringOrNull(organization.name) ??
      asStringOrNull(tenantOrganization.name) ??
      asStringOrNull(normalizedDesignProps?.companyName),
    tenantOrganizationLogoUrl:
      asStringOrNull(source.tenantOrganizationLogoUrl) ??
      asStringOrNull(source.organizationLogoUrl) ??
      asStringOrNull(organization.logoUrl) ??
      asStringOrNull(tenantOrganization.logoUrl) ??
      asStringOrNull(normalizedDesignProps?.logoUrl),
    colorPrimary: asStringOrNull(source.colorPrimary),
    colorPrimaryLighter: asStringOrNull(source.colorPrimaryLighter),
    heroTitle: asStringOrNull(source.heroTitle),
    heroSubtitle: asStringOrNull(source.heroSubtitle)
  };
};

export const normalizeArtifactShareListResponse = (response: ArtifactShareEnvelope): ArtifactShare[] => {
  const data = asRecord(response.data);
  const shares = Array.isArray(data.shares)
    ? data.shares
    : Array.isArray(response.data)
      ? response.data
      : data.share
        ? [data.share]
        : [];
  const seen = new Set<string>();

  return shares.reduce<ArtifactShare[]>((result, shareValue) => {
    const share = normalizeArtifactShare(shareValue);

    if (!share || seen.has(share.id)) {
      return result;
    }

    seen.add(share.id);
    result.push(share);
    return result;
  }, []);
};

export const normalizePublicArtifactCollectionResponse = (
  response: PublicArtifactCollectionEnvelope
): PublicArtifactCollection => {
  const data = asRecord(response.data);
  const patient = asRecord(data.patient);
  const shareSource = asRecord(data.share);
  const downloadZipUrl =
    pickFirstString(data, ['downloadZipUrl', 'artifactsZipUrl', 'downloadArchiveUrl']) ??
    pickFirstString(shareSource, ['downloadZipUrl', 'artifactsZipUrl', 'downloadArchiveUrl']);
  const brandingSource = {
    ...data,
    ...shareSource,
    ...patient,
    ...asRecord(data.branding),
    ...asRecord(data.designProps),
    ...asRecord(shareSource.designProps),
    ...asRecord(patient.designProps),
    ...asRecord(asRecord(data.branding).designProps)
  };
  const branding = normalizePublicArtifactBranding(brandingSource);
  const collectionSource = asRecord(data.collection);
  const collection = Object.keys(collectionSource).length
    ? normalizeArtifactCollectionResponse({ data: collectionSource })
    : normalizeArtifactCollectionResponse({ data });

  return {
    share: normalizeArtifactShare(data.share),
    signupCode: hasOwn(data, 'signupCode')
      ? data.signupCode === null
        ? null
        : pickFirstString(data, ['signupCode'])
      : undefined,
    allowDownloads: data.allowDownloads === true ? true : undefined,
    downloadZipUrl: downloadZipUrl ? resolveMediaUrl(downloadZipUrl) : undefined,
    collection,
    branding
  };
};

export type CreateMyArtifactPayload = {
  title: string;
  description?: string | null;
  memoryDate?: string | null;
  memoryDateLabel?: string | null;
  imageMediaId: string;
  audioMediaId?: string | null;
};

export type UpdateMyArtifactPayload = {
  artifactId: string;
  expectedRevision: number;
  patch: Partial<{
    title: string;
    description: string | null;
    memoryDate: string | null;
    memoryDateLabel: string | null;
    imageMediaId: string | null;
    audioMediaId: string | null;
    transcriptText: string | null;
  }>;
};

export type DeleteMyArtifactPayload = {
  artifactId: string;
  expectedRevision: number;
};

export type ReorderMyArtifactsPayload = {
  items: Array<{
    artifactId: string;
    displayOrder: number;
    expectedRevision: number;
  }>;
  expectedCollectionRevision: number;
};

export type RetryMyArtifactTranscriptionPayload = {
  artifactId: string;
  expectedRevision: number;
};

export type CreateArtifactMediaUploadPayload = {
  mediaKind: ArtifactMediaKind;
  contentType: string;
  fileName?: string;
  fileSizeBytes?: number;
};

export type CompleteArtifactMediaUploadPayload = {
  uploadId: string;
};

export type ManageMyArtifactShareAction = 'CREATE' | 'PREVIEW' | 'UPDATE_EXPIRY' | 'REVOKE' | 'REGENERATE' | 'RESEND';

export type ManageMyArtifactSharePayload = {
  action: ManageMyArtifactShareAction;
  shareId?: string;
  expiresAt?: string | null;
  recipientName?: string;
  recipientEmail?: string;
};

const toNullableBodyValue = <T>(value: T | undefined): T | null | undefined =>
  value === undefined ? undefined : (value ?? null);

export const getMyArtifactsRequestConfig = (): ApiRequestConfig => ({
  url: '/users/me/artifacts'
});

export const toCreateMyArtifactRequest = ({
  title,
  description,
  memoryDate,
  memoryDateLabel,
  imageMediaId,
  audioMediaId
}: CreateMyArtifactPayload) => ({
  photoUploadId: imageMediaId,
  title,
  description: description ?? null,
  memoryDate: memoryDate ?? null,
  dateLabel: memoryDateLabel ?? null,
  audioUploadId: audioMediaId ?? null
});

export const toUpdateMyArtifactRequest = ({ expectedRevision, patch }: UpdateMyArtifactPayload) => {
  const body: Record<string, unknown> = {
    expectedRevision
  };

  if ('title' in patch) {
    body.title = patch.title;
  }

  if ('description' in patch) {
    body.description = toNullableBodyValue(patch.description);
  }

  if ('transcriptText' in patch) {
    body.editedTranscriptText = toNullableBodyValue(patch.transcriptText);
  }

  if ('memoryDate' in patch) {
    body.memoryDate = toNullableBodyValue(patch.memoryDate);
  }

  if ('memoryDateLabel' in patch) {
    body.dateLabel = toNullableBodyValue(patch.memoryDateLabel);
  }

  if ('imageMediaId' in patch) {
    body.photoUploadId = toNullableBodyValue(patch.imageMediaId);
  }

  if ('audioMediaId' in patch) {
    body.audioUploadId = toNullableBodyValue(patch.audioMediaId);
  }

  return body;
};

export const toDeleteMyArtifactRequest = ({ expectedRevision }: DeleteMyArtifactPayload) => ({
  expectedRevision
});

export const toReorderMyArtifactsRequest = ({ items, expectedCollectionRevision }: ReorderMyArtifactsPayload) => ({
  expectedRevision: expectedCollectionRevision,
  orderedArtifactIds: [...items]
    .sort((left, right) => left.displayOrder - right.displayOrder)
    .map((item) => item.artifactId)
});

export const toRetryMyArtifactTranscriptionRequest = (_payload: RetryMyArtifactTranscriptionPayload) => ({});

export const toRetryMyArtifactTranscriptionRequestConfig = ({
  artifactId
}: RetryMyArtifactTranscriptionPayload): ApiRequestConfig => ({
  url: `/users/me/artifacts/${encodeURIComponent(artifactId)}/retry-transcription`,
  method: 'POST'
});

export const toCreateArtifactMediaUploadRequest = ({
  mediaKind,
  fileName,
  contentType,
  fileSizeBytes
}: CreateArtifactMediaUploadPayload) => ({
  mediaType: mediaKind === 'AUDIO' ? 'audio' : 'photo',
  fileName,
  contentType,
  fileSizeBytes
});

export const toManageMyArtifactShareRequest = ({
  expiresAt,
  recipientName,
  recipientEmail
}: ManageMyArtifactSharePayload) => {
  const body: Record<string, string | boolean | null> = {};

  body.allowDownloads = true;

  if (expiresAt !== undefined) {
    body.expiresAt = expiresAt;
  }

  if (recipientName !== undefined) {
    body.recipientName = recipientName;
  }

  if (recipientEmail !== undefined) {
    body.recipientEmail = recipientEmail;
  }

  return body;
};

export const toManageMyArtifactShareMutationRequest = ({
  action,
  shareId,
  expiresAt,
  recipientName,
  recipientEmail
}: ManageMyArtifactSharePayload): ApiRequestConfig => {
  const encodedShareId = encodeURIComponent(shareId || '');

  switch (action) {
    case 'CREATE':
      return {
        url: '/users/me/artifact-shares',
        method: 'POST',
        body: toManageMyArtifactShareRequest({ action, shareId, expiresAt, recipientName, recipientEmail })
      };
    case 'PREVIEW':
      return {
        url: '/users/me/artifact-shares/preview'
      };
    case 'UPDATE_EXPIRY':
      return {
        url: `/users/me/artifact-shares/${encodedShareId}`,
        method: 'PATCH',
        body: {
          allowDownloads: true,
          expiresAt: expiresAt ?? null
        }
      };
    case 'REVOKE':
      return {
        url: `/users/me/artifact-shares/${encodedShareId}/revoke`,
        method: 'POST'
      };
    case 'RESEND':
      return {
        url: `/users/me/artifact-shares/${encodedShareId}/resend`,
        method: 'POST'
      };
    case 'REGENERATE':
    default:
      return {
        url: `/users/me/artifact-shares/${encodedShareId}/regenerate`,
        method: 'POST'
      };
  }
};

export const artifactsApi = createApi({
  reducerPath: 'artifactsApi',
  baseQuery: baseQueryWithReAuth,
  endpoints: (build) => ({
    getMyArtifacts: build.query<ArtifactCollection, void>({
      query: () => getMyArtifactsRequestConfig(),
      transformResponse: (response: ArtifactCollectionEnvelope) => normalizeArtifactCollectionResponse(response)
    }),
    createMyArtifact: build.mutation<Artifact, CreateMyArtifactPayload>({
      query: (body) => ({
        url: '/users/me/artifacts',
        method: 'POST',
        body: toCreateMyArtifactRequest(body)
      }),
      transformResponse: (response: ArtifactEnvelope) => normalizeArtifact(asRecord(response.data))
    }),
    updateMyArtifact: build.mutation<Artifact, UpdateMyArtifactPayload>({
      query: ({ artifactId, ...body }) => ({
        url: `/users/me/artifacts/${encodeURIComponent(artifactId)}`,
        method: 'PATCH',
        body: toUpdateMyArtifactRequest({ artifactId, ...body })
      }),
      transformResponse: (response: ArtifactEnvelope) => normalizeArtifact(asRecord(response.data))
    }),
    deleteMyArtifact: build.mutation<{ success: boolean }, DeleteMyArtifactPayload>({
      query: ({ artifactId, ...body }) => ({
        url: `/users/me/artifacts/${encodeURIComponent(artifactId)}`,
        method: 'DELETE',
        body: toDeleteMyArtifactRequest({ artifactId, ...body })
      }),
      transformResponse: (response: SuccessEnvelope) => ({ success: Boolean(response.success) })
    }),
    reorderMyArtifacts: build.mutation<ArtifactCollection, ReorderMyArtifactsPayload>({
      query: (body) => ({
        url: '/users/me/artifacts/reorder',
        method: 'POST',
        body: toReorderMyArtifactsRequest(body)
      }),
      transformResponse: (response: ArtifactCollectionEnvelope) => normalizeArtifactCollectionResponse(response)
    }),
    retryMyArtifactTranscription: build.mutation<Artifact, RetryMyArtifactTranscriptionPayload>({
      query: (body) => toRetryMyArtifactTranscriptionRequestConfig(body),
      transformResponse: (response: ArtifactEnvelope) => normalizeArtifact(asRecord(response.data))
    }),
    createArtifactMediaUpload: build.mutation<ArtifactMediaUpload, CreateArtifactMediaUploadPayload>({
      query: (body) => ({
        url: '/users/me/artifacts/upload-intents',
        method: 'POST',
        body: toCreateArtifactMediaUploadRequest(body)
      }),
      transformResponse: (response: ArtifactMediaUploadEnvelope) => normalizeArtifactMediaUpload(response.data)
    }),
    completeArtifactMediaUpload: build.mutation<ArtifactMediaUpload, CompleteArtifactMediaUploadPayload>({
      query: ({ uploadId }) => ({
        url: `/users/me/artifacts/upload-intents/${encodeURIComponent(uploadId)}/finalize`,
        method: 'POST'
      }),
      transformResponse: (response: ArtifactMediaUploadEnvelope) => normalizeArtifactMediaUpload(response.data)
    }),
    getArtifactMediaUploadStatus: build.query<ArtifactMediaUpload, { uploadId: string }>({
      query: ({ uploadId }) => ({
        url: `/users/me/artifacts/upload-intents/${encodeURIComponent(uploadId)}`
      }),
      transformResponse: (response: ArtifactMediaUploadEnvelope) => normalizeArtifactMediaUpload(response.data)
    }),
    getMyArtifactShares: build.query<ArtifactShare[], void>({
      query: () => ({
        url: '/users/me/artifact-shares'
      }),
      transformResponse: (response: ArtifactShareEnvelope) => normalizeArtifactShareListResponse(response)
    }),
    getMySharedArtifactPreview: build.query<PublicArtifactCollection, void>({
      query: () => ({
        url: '/users/me/artifact-shares/preview'
      }),
      transformResponse: (response: PublicArtifactCollectionEnvelope) =>
        normalizePublicArtifactCollectionResponse(response)
    }),
    manageMyArtifactShare: build.mutation<ArtifactShare | null, ManageMyArtifactSharePayload>({
      query: (body) => toManageMyArtifactShareMutationRequest(body),
      transformResponse: (response: ArtifactEnvelope) =>
        normalizeArtifactShare(asRecord(response.data).share ?? asRecord(response.data).artifactShare ?? response.data)
    }),
    getPublicArtifactCollection: build.query<PublicArtifactCollection, string>({
      query: (token) => ({
        url: `/public/artifacts/${encodeURIComponent(token)}`
      }),
      transformResponse: (response: PublicArtifactCollectionEnvelope) =>
        normalizePublicArtifactCollectionResponse(response)
    })
  })
});

export const {
  useGetMyArtifactsQuery,
  useCreateMyArtifactMutation,
  useUpdateMyArtifactMutation,
  useDeleteMyArtifactMutation,
  useReorderMyArtifactsMutation,
  useRetryMyArtifactTranscriptionMutation,
  useCreateArtifactMediaUploadMutation,
  useCompleteArtifactMediaUploadMutation,
  useLazyGetArtifactMediaUploadStatusQuery,
  useGetArtifactMediaUploadStatusQuery,
  useGetMyArtifactSharesQuery,
  useGetMySharedArtifactPreviewQuery,
  useManageMyArtifactShareMutation,
  useGetPublicArtifactCollectionQuery
} = artifactsApi;
